import React, { useState, useEffect, useRef } from 'react';
import { IconButton, CircularProgress, Box, Typography } from '@mui/material';

/**
 * CooldownButton - An icon button that disables itself for a specified duration after being clicked
 *
 * @param {React.ReactNode} icon - The icon to display in the button
 * @param {function} onClick - Callback function when button is clicked
 * @param {number} cooldownSeconds - Duration in seconds the button should be disabled after click (default: 10)
 * @param {boolean} disabled - External disabled state (optional)
 * @param {string} ariaLabel - Accessibility label for the button
 * @param {object} sx - Additional MUI sx styling
 */
export default function CooldownButton({
	icon,
	onClick,
	cooldownSeconds = 10,
	disabled = false,
	ariaLabel = 'button',
	timerText = true,
	sx = {},
	...otherProps
}) {
	const [isCooldown, setIsCooldown] = useState(false);
	const [remainingTime, setRemainingTime] = useState(0);

	const timerRef = useRef(null);
	const startTimeRef = useRef(null);

	// Button size in pixels (used for CircularProgress sizing)
	const buttonSize = 32;

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
		};
	}, []);

	const handleClick = (event) => {
		if (isCooldown || disabled) { return; }

		// Call the provided onClick handler
		if (onClick) { onClick(event); }

		// Start cooldown
		setIsCooldown(true);
		setRemainingTime(cooldownSeconds);
		startTimeRef.current = Date.now();

		// Update remaining time every 100ms for smooth countdown
		timerRef.current = setInterval(() => {
			const elapsed = (Date.now() - startTimeRef.current) / 1000;
			const remaining = Math.max(0, cooldownSeconds - elapsed);

			if (remaining <= 0) {
				// Cooldown complete
				setIsCooldown(false);
				setRemainingTime(0);
				clearInterval(timerRef.current);
				timerRef.current = null;
			} else {
				setRemainingTime(remaining);
			}
		}, 100);
	};

	const isDisabled = disabled || isCooldown;
	const progress = isCooldown ? ((cooldownSeconds - remainingTime) / cooldownSeconds) * 100 : 0;

	return (
		<Box sx={{ position: 'relative', display: 'inline-flex' }}>
			<IconButton
				onClick={handleClick}
				disabled={isDisabled}
				color='primary'
				size='small'
				aria-label={ariaLabel}
				sx={{
					backgroundColor: 'primary.main',
					color: 'white',
					'&:hover': {
						backgroundColor: 'primary.dark',
					},
					'&.Mui-disabled': {
						backgroundColor: 'primary.light',
						color: 'rgba(255, 255, 255, 0.25)',
					},
					...sx
				}}
				{...otherProps}
			>
				{icon}
			</IconButton>
			{ isCooldown && (
				<CircularProgress
					variant="determinate"
					value={progress}
					size={buttonSize}
					thickness={22}  // This thickness fills the entire button
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						marginTop: `${-(buttonSize / 2)}px`,
						marginLeft: `${-(buttonSize / 2)}px`,
						color: 'primary.main',
					}}
				/>
			)}
			{ (timerText && isCooldown) && (
				<Typography
					variant="caption"
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						color: 'white',
						fontWeight: 'bold',
						fontSize: '1.25rem',
						fontFamily: 'Saira, sans-serif',
						pointerEvents: 'none',
					}}
				>
					{Math.ceil(remainingTime)}
				</Typography>
			)}
		</Box>
	);
}
